import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import { useFiltersTheme } from '../../filters-theme-provider';
import { flattenColor } from '../../utils';

export type ClearFiltersButtonProps =
  ButtonHTMLAttributes<HTMLButtonElement> & { withBackground?: boolean };

export const ClearFiltersButton = forwardRef<
  HTMLButtonElement,
  ClearFiltersButtonProps
>(
  (
    { children, onClick, withBackground, ...forwardingProps },
    forwardingRef
  ) => {
    const { textColor, accentColor } = useFiltersTheme();

    return (
      <Button
        {...forwardingProps}
        $textColor={textColor}
        $accentColor={accentColor}
        $withBackground={withBackground}
        onClick={onClick}
        ref={forwardingRef}
      >
        {children}
      </Button>
    );
  }
);

const Button = styled.button.attrs({
  className: createReadableClass({
    component: 'clear-filters-button',
    element: 'button'
  })
})<{
  $textColor: string;
  $accentColor: string;
  $withBackground?: boolean;
}>`
  cursor: pointer;
  padding: 8px 16px;
  text-align: center;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid transparent;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  ${({ $textColor, $withBackground, $accentColor }) => css`
    color: ${getColorWithAlpha($textColor, 0.5)};
    background-color: ${flattenColor(
      getColorWithAlpha($textColor, 0),
      $withBackground ? getInvertedColor($textColor) : undefined
    )};

    ${focusVisibleMixin($accentColor)}

    &:hover,
    &:focus-visible {
      color: ${getColorWithAlpha($textColor, 0.7)};
      background-color: ${flattenColor(
        getColorWithAlpha($textColor, 0.05),
        $withBackground ? getInvertedColor($textColor) : undefined
      )};
    }
  `}
`;
